import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useRegister } from '@/api/api-hooks/auth-hooks';
import Popup from '@/components/elements/Popup';
import { LabelInput } from '@/components/forms/LabelInput';
import { PasswordInput, PasswordRegExp } from '@/components/forms/PasswordInput';
import AppButton from '@/components/ui/Button';

interface Inputs {
  name: string;
  username: string;
  email: string;
  company: string;
  password: string;
  confirmPassword: string;
}

function SignUpForm({ closeForm }: { closeForm: () => void }) {
  const { mutate: registerHandle, data, isSuccess, isPending } = useRegister();
  const [openPopup, setOpenPopup] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<Inputs>({
    mode: 'onChange',
  });
  const onSubmit: SubmitHandler<Inputs> = (data) => {
    registerHandle({
      name: data.name,
      username: data.username,
      password: data.password,
      company: data.company,
      email: data.email,
    });
  };

  useEffect(() => {
    if (data && isSuccess) {
      if (data?.error || data?.statusCode == 500) {
        setTitle('Error!');
      } else {
        setTitle('Success Sign up');
        reset();
      }
      setOpenPopup(true);
    }
  }, [data, isSuccess, reset]);

  const closeMessagePopup = () => {
    setOpenPopup(false);
    if (!data?.error && data?.statusCode !== 500) {
      closeForm();
    }
  };

  const password = watch('password');

  return (
    <>
      <div className="h-full">
        <form onSubmit={handleSubmit(onSubmit)} className="h-full">
          <div className="flex h-[calc(100%-80px)] w-full flex-col justify-between">
            <div className="p-4 md:p-6">
              <LabelInput
                name="name"
                labelText="Name"
                placeholder="Enter your name"
                id="name"
                otherProps={{
                  ...register('name', {
                    required: 'Name is required',
                    minLength: { value: 2, message: 'Too short name' },
                    maxLength: { value: 30, message: 'Too long name' },
                  }),
                }}
                error={errors.name?.message}
              />
              <LabelInput
                name="username"
                labelText="Username"
                placeholder="Enter your username"
                id="username"
                otherProps={{
                  ...register('username', {
                    minLength: { value: 5, message: 'Too short username' },
                    maxLength: { value: 15, message: 'Too long username' },
                  }),
                }}
                error={errors.username?.message}
              />
              <LabelInput
                name="email"
                labelText="Email"
                placeholder="Enter your email"
                id="email"
                otherProps={{
                  ...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'Email is not correct',
                    },
                    maxLength: { value: 320, message: 'Too long email' },
                  }),
                }}
                error={errors.email?.message}
              />
              <LabelInput
                name="company"
                labelText="Company"
                placeholder="Enter your company"
                id="company"
                otherProps={{
                  ...register('company', {
                    minLength: { value: 2, message: 'Too short company name' },
                    maxLength: { value: 35, message: 'Too long company name' },
                  }),
                }}
                error={errors.company?.message}
              />
              <div className="flex w-full flex-wrap justify-end md:flex-nowrap">
                <div className="mr-0 w-full md:mr-3">
                  <PasswordInput
                    labelText="Password"
                    id="password"
                    placeholder="Enter your password"
                    name="password"
                    otherProps={{
                      ...register('password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Too small password' },
                        maxLength: { value: 40, message: 'Too long password' },
                        pattern: {
                          value: PasswordRegExp,
                          message:
                            'Must include upper case letter, one lower case letter, one number',
                        },
                      }),
                    }}
                    error={errors.password?.message}
                    wrapErrorText={true}
                    classNameContainer="!mb-1.5"
                  />
                </div>
                <div className="ml-0 w-full md:ml-3">
                  <PasswordInput
                    labelText="Confirm Password"
                    id="confirmPassword"
                    placeholder="Enter your password again"
                    name="confirmPassword"
                    otherProps={{
                      ...register('confirmPassword', {
                        required: 'Password confirmation is required',
                        minLength: { value: 8, message: 'Too small password' },
                        maxLength: { value: 40, message: 'Too long password' },
                        pattern: {
                          value: PasswordRegExp,
                          message:
                            'Must include upper case letter, one lower case letter, one number',
                        },
                        validate: (value) => value === password || 'Passwords do not match',
                      }),
                    }}
                    error={errors.confirmPassword?.message}
                    wrapErrorText={true}
                  />
                </div>
              </div>
            </div>
            <div className="flex w-full items-center justify-end p-5">
              <AppButton onClick={closeForm} variant="secondary" className="mr-4">
                Cancel
              </AppButton>
              <AppButton disabled={isPending} type="submit" loading={isPending}>
                Sign up
              </AppButton>
            </div>
          </div>
        </form>
      </div>
      <Popup isOpen={openPopup} onClose={closeMessagePopup} title={title}>
        <p>{data?.message}</p>
      </Popup>
    </>
  );
}

export default SignUpForm;
